import { useTranslation } from "react-i18next";
// src/pages/VendorEntry.tsx
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiPost } from "../api/client";
import { Button } from "../components/Button";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { companyRoute } from "../utils/routeHelpers";
import Inlay from "../components/Inlay";
import { A } from "../styles/appleClasses";

/** ================= Types ================= */
type Vendor = {
  id: number | string;
  name: string;
  email?: string | null;
  mobile?: string | null;
  contact?: string | null;
  address?: string | null;
};

type VendorFormState = {
  name: string;
  email: string;
  mobile: string;
  contact: string;
  address: string;
};

type CommonProps = {
  variant?: "minimal" | "full";
  companyId?: string | number;
  initial?: Partial<VendorFormState>;
  onCreated?: (vendor: Vendor) => void;
  title?: string;
  autoFocusName?: boolean;
};

type PageModeProps = CommonProps & { mode?: "page" };
type InlayModeProps = CommonProps & { mode: "inlay"; onClose: () => void };
type Props = PageModeProps | InlayModeProps;

/** ================= Utils ================= */
function isValidEmail(email: string) {
  return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// textarea: same as A.inputBase but without fixed height + with padding-y
const textareaBase = A.inputBase
  .replace("h-10 ", "")
  .replace("px-4 ", "px-4 py-2 ");

// Prevent iPhone/Safari modal auto-zoom on inlay inputs
const modalInputBase = `${A.inputBase} text-[16px] sm:text-[13px]`;
const modalTextareaBase = `${textareaBase} text-[16px] sm:text-[13px]`;

/** ================= Component ================= */
type DisplayError = { local: "nameRequired" | "invalidEmail" | "createFailed" } | { raw: string } | null;

export default function VendorEntry(props: Props) {
  const { t } = useTranslation();
  const errorText = (error: DisplayError): string | null => error == null ? null : "raw" in error ? error.raw : ({
    nameRequired: t("directory.vendors.nameRequired"),
    invalidEmail: t("directory.vendors.invalidEmail"),
    createFailed: t("directory.vendors.createFailed"),
  })[error.local];

  const {
    variant = "full",
    companyId,
    initial,
    onCreated,
    title = t("directory.vendors.new"),
    autoFocusName = true,
  } = props;

  const navigate = useNavigate();
  const { company_id: routeCompanyId } = useParams<{ company_id?: string }>();
  const resolvedCompanyId = useMemo(
    () => (companyId ?? routeCompanyId ?? "").toString(),
    [companyId, routeCompanyId]
  );

  const isInlay = props.mode === "inlay";

  const [form, setForm] = useState<VendorFormState>({
    name: initial?.name ?? "",
    email: initial?.email ?? "",
    mobile: initial?.mobile ?? "",
    contact: initial?.contact ?? "",
    address: initial?.address ?? "",
  });

  const [error, setError] = useState<DisplayError>(null);
  const [saving, setSaving] = useState(false);

  const goList = () => {
    if (resolvedCompanyId) navigate(companyRoute(resolvedCompanyId, "/vendors"));
    else navigate(-1);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  async function createVendor(): Promise<Vendor> {
    const query = resolvedCompanyId
      ? `?company_id=${encodeURIComponent(resolvedCompanyId)}`
      : "";
    const payload = {
      name: form.name.trim(),
      email: form.email.trim() || null,
      mobile: form.mobile.trim() || null,
      contact: form.contact.trim() || null,
      address: form.address.trim() || null,
    };
    return apiPost(`/vendors${query}`, payload);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    setError(null);

    if (!form.name.trim()) {
      setError({ local: "nameRequired" });
      return;
    }
    if (!isValidEmail(form.email.trim())) {
      setError({ local: "invalidEmail" });
      return;
    }

    setSaving(true);
    try {
      const vendor = (await createVendor()) as Vendor;
      onCreated?.(vendor);

      if (isInlay) {
        props.onClose();
      } else {
        goList();
      }
    } catch {
      setError({ local: "createFailed" });
    } finally {
      setSaving(false);
    }
  };

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    `hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD`;

  const inputCls = isInlay ? modalInputBase : A.inputBase;
  const textareaCls = isInlay ? modalTextareaBase : textareaBase;

  /** Reusable form body */
  const renderFormBody = (cancelHandler: () => void) => (
    <form
      onSubmit={handleSubmit}
      className={`space-y-3 ${saving ? "opacity-60 pointer-events-none" : ""}`}
      autoComplete="off"
    >
      {/* Vendor Name (always) */}
      <div>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          className={inputCls}
          placeholder={t("directory.vendors.namePlaceholder")}
          autoFocus={autoFocusName && !isInlay}
          required
          aria-label={t("directory.vendors.nameAria")}
          disabled={saving}
        />
      </div>

      {/* Variant-specific fields */}
      {variant === "minimal" ? (
        <div>
          <input
            type="text"
            name="contact"
            value={form.contact}
            onChange={handleChange}
            className={inputCls}
            placeholder={t("directory.vendors.contactOptional")}
            aria-label={t("directory.common.contactPerson")}
            disabled={saving}
          />
        </div>
      ) : (
        <>
          <div>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className={inputCls}
              placeholder={t("directory.vendors.emailOptional")}
              autoComplete="email"
              aria-label={t("directory.common.emailAddress")}
              disabled={saving}
            />
          </div>

          <div>
            <input
              type="tel"
              name="mobile"
              value={form.mobile}
              onChange={handleChange}
              className={inputCls}
              placeholder={t("directory.vendors.mobileOptional")}
              autoComplete="tel"
              aria-label={t("directory.common.mobileNumber")}
              disabled={saving}
            />
          </div>

          <div>
            <input
              type="text"
              name="contact"
              value={form.contact}
              onChange={handleChange}
              className={inputCls}
              placeholder={t("directory.vendors.contactOptional")}
              aria-label={t("directory.common.contactPerson")}
              disabled={saving}
            />
          </div>

          <div>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              rows={3}
              className={textareaCls}
              placeholder={t("directory.common.addressOptional")}
              aria-label={t("directory.common.address")}
              disabled={saving}
            />
          </div>
        </>
      )}

      {/* Error slot (no layout shift) */}
      <div className="min-h-[18px]">
        {error ? (
          <div className="text-xs text-red-500 font-medium">{errorText(error)}</div>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-2">
        <Button
          type="button"
          variant="secondary"
          onClick={cancelHandler}
          disabled={saving}
        >
          {t("directory.common.cancel")}
        </Button>

        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? t("directory.common.savingDots") : t("directory.common.save")}
        </Button>
      </div>
    </form>
  );

  /** ===== Render per mode ===== */
  if (isInlay) {
    return (
      <Inlay backLabel={t("directory.common.back")} closeLabel={t("directory.common.close")} title={title} onClose={props.onClose} onBack={props.onClose}>
        <div className={`p-4 sm:p-6 ${A.body}`}>
          <div className={`${A.card} p-4 sm:p-6`}>
            {renderFormBody(props.onClose)}
          </div>
        </div>
      </Inlay>
    );
  }

  // Page mode
  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      <div className="w-full max-w-xl mx-auto px-2 sm:px-6 pt-4 pb-10">
        {/* Header (match list/detail: ← title ×) */}
        <div className="sticky top-0 z-30 bg-gray-50 border-b border-gray-100 px-2 pb-2 mb-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={iconBtn}
              onClick={() => navigate(-1)}
              aria-label={t("directory.common.back")}
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
            </button>

            <h2 className={`flex-1 text-center ${A.title}`}>{title}</h2>

            <button
              type="button"
              className={iconBtn}
              onClick={goList}
              aria-label={t("directory.common.close")}
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={`${A.card} p-4 sm:p-6`}>
          {renderFormBody(() => navigate(-1))}
        </div>
      </div>
    </div>
  );
}